import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { API_BASE } from "../lib/api";
import { CheckCircle2, XCircle, Loader2, ShieldCheck } from "lucide-react";
import axios from "axios";

export default function Verify() {
    const { matricola } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        (async () => {
            try {
                // Public endpoint — no auth needed
                const res = await axios.get(`${API_BASE}/public/verify/${encodeURIComponent(matricola)}`);
                setData(res.data);
            } catch (e) {
                setError("Errore nella verifica del tesserino.");
            } finally {
                setLoading(false);
            }
        })();
    }, [matricola]);

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F4F6FB] flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
        );
    }

    const isValid = data && data.status === "valid";

    return (
        <div className="min-h-screen bg-[#F4F6FB]" data-testid="verify-page">
            <header className="border-b border-slate-200 bg-white">
                <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-4 sm:px-6 lg:px-8">
                    <img
                        src="/brand/logo-full.jpg"
                        alt="Splendor Sud"
                        className="h-12 w-auto"
                    />
                    <div className="ml-auto text-right leading-tight">
                        <div className="text-[11px] uppercase tracking-widest text-slate-500">
                            Verifica Pubblica
                        </div>
                        <div className="font-display text-sm font-bold text-[#376DB3]">
                            Tesserino Identificativo
                        </div>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
                {error ? (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-red-700">
                        {error}
                    </div>
                ) : isValid ? (
                    <ValidCard data={data} />
                ) : (
                    <InvalidCard matricola={matricola} message={data?.message} />
                )}

                <div className="mt-8 text-center text-xs text-slate-500">
                    Pagina di verifica pubblica · {new Date().toLocaleString("it-IT")}
                </div>
            </main>
        </div>
    );
}

const ValidCard = ({ data }) => {
    return (
        <div className="ss-fade-up overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="verify-valid">
            <div className="flex items-center gap-3 border-b border-emerald-100 bg-emerald-50 px-6 py-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-600 text-white">
                    <CheckCircle2 className="h-5 w-5" />
                </div>
                <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-emerald-700">
                        Tesserino Verificato
                    </div>
                    <div className="font-display text-lg font-bold text-emerald-900">
                        Identità autenticata
                    </div>
                </div>
                <ShieldCheck className="ml-auto h-6 w-6 text-emerald-600" />
            </div>

            <div className="grid gap-6 p-6 sm:grid-cols-[140px_1fr]">
                <div>
                    {data.photo ? (
                        <img
                            src={data.photo}
                            alt=""
                            className="h-40 w-32 rounded-md object-cover ring-1 ring-slate-200"
                        />
                    ) : (
                        <div className="flex h-40 w-32 items-center justify-center rounded-md bg-slate-100 text-xs uppercase text-slate-400">
                            No foto
                        </div>
                    )}
                </div>
                <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                        Dipendente
                    </div>
                    <h1 className="mt-1 font-display text-2xl font-bold text-[#376DB3] sm:text-3xl">
                        {data.cognome} {data.nome}
                    </h1>
                    <div className="mt-3 space-y-2 text-sm">
                        <Row label="Matricola" value={data.matricola} mono testId="verify-matricola" />
                        <Row label="Qualifica" value={data.qualifica} testId="verify-qualifica" />
                        <Row label="Data assunzione" value={fmtDate(data.data_assunzione)} mono />
                    </div>

                    <div className="my-5 border-t border-slate-200" />

                    <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Azienda</div>
                    <div className="mt-1 font-display font-bold text-[#376DB3]">
                        {data.company?.ragione_sociale}
                    </div>
                    <div className="text-sm text-slate-600">{data.company?.tagline}</div>
                    <div className="text-sm text-slate-600">{data.company?.sede}</div>
                    <div className="text-sm text-slate-600 font-mono-numbers">
                        P.IVA {data.company?.p_iva}
                    </div>
                </div>
            </div>
        </div>
    );
};

const InvalidCard = ({ matricola, message }) => (
    <div className="ss-fade-up overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="verify-invalid">
        <div className="flex items-center gap-3 border-b border-red-100 bg-red-50 px-6 py-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-600 text-white">
                <XCircle className="h-5 w-5" />
            </div>
            <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-red-700">
                    Tesserino non riconosciuto
                </div>
                <div className="font-display text-lg font-bold text-red-900">
                    Verifica fallita
                </div>
            </div>
        </div>
        <div className="p-6">
            <p className="text-sm text-slate-700">
                {message || "Nessun tesserino attivo trovato per questa matricola."}
            </p>
            <p className="mt-2 text-sm text-slate-700">
                Matricola cercata: <span className="font-mono-numbers font-semibold">{matricola}</span>
            </p>
            <p className="mt-4 text-xs text-slate-500">
                Se sei l'amministratore, accedi al gestionale per verificare l'esistenza del dipendente.
            </p>
            <Link
                to="/login"
                className="mt-6 inline-block rounded-md bg-[#376DB3] px-4 py-2 text-sm font-medium text-white hover:bg-[#376DB3]/90"
            >
                Vai al gestionale
            </Link>
        </div>
    </div>
);

const Row = ({ label, value, mono, testId }) => (
    <div className="flex justify-between gap-4 border-b border-slate-100 pb-1.5 last:border-b-0">
        <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
        <div
            className={`text-sm font-medium text-[#376DB3] ${mono ? "font-mono-numbers" : ""}`}
            data-testid={testId}
        >
            {value || "—"}
        </div>
    </div>
);

const fmtDate = (s) => {
    if (!s) return "—";
    const p = s.split("-");
    if (p.length !== 3) return s;
    return `${p[2]}/${p[1]}/${p[0]}`;
};
